import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from 'constants/constants';

type ModalState = Record<string, unknown>;

/**
 * Opens a modal route while recording the current page as `backgroundLocation`,
 * so it renders (dimmed) beneath the modal overlay. Any existing state (e.g.
 * `lineage`, `editCategory`, `editLineage`) is preserved and merged.
 *
 * Usage: `const openModal = useModalNavigate();`
 *        `openModal(ROUTES.createCategory, { lineage });`
 */
export function useModalNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (to: string, state?: ModalState) => {
      navigate(to, { state: { ...state, backgroundLocation: location } });
    },
    [navigate, location],
  );
}

/**
 * Closes a modal route: returns to the previous page, or the library home if the
 * modal was opened via a direct deep-link (no `backgroundLocation` in history).
 */
export function useModalClose() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasBackground = Boolean(
    (location.state as { backgroundLocation?: unknown } | null)?.backgroundLocation,
  );
  return useCallback(() => {
    if (hasBackground) navigate(-1);
    else navigate(ROUTES.library);
  }, [navigate, hasBackground]);
}
